import { normalizeLocale, type SupportedLocale } from './config';

/**
 * Connect (Work Chats) activity copy: app-wide toasts, the unread bar, the
 * jump-to-present count, Mark unread, Join from Mentions, and DM presence.
 */
type ConnectNotifyCopy = {
  /** "{name} mentioned you in {channel}" */
  mention: (name: string, channel: string) => string;
  /** A mention inside a DM. */
  mentionDm: (name: string) => string;
  /** Mention whose author is unknown (imai or a legacy row). */
  mentionAnon: (channel: string) => string;
  /** A new direct message. */
  dm: (name: string) => string;
  /** A new message in an all-messages channel. */
  message: (name: string, channel: string) => string;
  open: string;
  /** "{count} new messages since {time}" */
  unreadSince: (count: number, time: string) => string;
  markAsRead: string;
  /** Jump-to-present pill while new messages arrived below. */
  newBelow: (count: number) => string;
  markUnread: string;
  join: string;
  notJoined: string;
  online: string;
  away: string;
  offline: string;
};

const plural = (count: number, one: string, other: string) =>
  count === 1 ? one : other;

const COPY: Record<SupportedLocale, ConnectNotifyCopy> = {
  en: {
    mention: (n, c) => `${n} mentioned you in ${c}`,
    mentionDm: (n) => `${n} mentioned you`,
    mentionAnon: (c) => `You were mentioned in ${c}`,
    dm: (n) => `${n} sent you a message`,
    message: (n, c) => `${n} posted in ${c}`,
    open: 'Open',
    unreadSince: (k, t) =>
      `${k} ${plural(k, 'new message', 'new messages')} since ${t}`,
    markAsRead: 'Mark as read',
    newBelow: (k) => `${k} ${plural(k, 'new message', 'new messages')}`,
    markUnread: 'Mark unread',
    join: 'Join',
    notJoined: 'Not joined',
    online: 'Online',
    away: 'Away',
    offline: 'Offline',
  },
  'pt-BR': {
    mention: (n, c) => `${n} mencionou você em ${c}`,
    mentionDm: (n) => `${n} mencionou você`,
    mentionAnon: (c) => `Você foi mencionado em ${c}`,
    dm: (n) => `${n} enviou uma mensagem para você`,
    message: (n, c) => `${n} publicou em ${c}`,
    open: 'Abrir',
    unreadSince: (k, t) =>
      `${k} ${plural(k, 'nova mensagem', 'novas mensagens')} desde ${t}`,
    markAsRead: 'Marcar como lida',
    newBelow: (k) => `${k} ${plural(k, 'nova mensagem', 'novas mensagens')}`,
    markUnread: 'Marcar como não lida',
    join: 'Entrar',
    notJoined: 'Você não participa',
    online: 'Online',
    away: 'Ausente',
    offline: 'Offline',
  },
  ar: {
    mention: (n, c) => `أشار إليك ${n} في ${c}`,
    mentionDm: (n) => `أشار إليك ${n}`,
    mentionAnon: (c) => `تمت الإشارة إليك في ${c}`,
    dm: (n) => `أرسل إليك ${n} رسالة`,
    message: (n, c) => `نشر ${n} في ${c}`,
    open: 'فتح',
    unreadSince: (k, t) => `${k} رسائل جديدة منذ ${t}`,
    markAsRead: 'وضع علامة كمقروءة',
    newBelow: (k) => `${k} رسائل جديدة`,
    markUnread: 'وضع علامة كغير مقروءة',
    join: 'انضمام',
    notJoined: 'غير منضم',
    online: 'متصل',
    away: 'بعيد',
    offline: 'غير متصل',
  },
  es: {
    mention: (n, c) => `${n} te mencionó en ${c}`,
    mentionDm: (n) => `${n} te mencionó`,
    mentionAnon: (c) => `Te mencionaron en ${c}`,
    dm: (n) => `${n} te envió un mensaje`,
    message: (n, c) => `${n} publicó en ${c}`,
    open: 'Abrir',
    unreadSince: (k, t) =>
      `${k} ${plural(k, 'mensaje nuevo', 'mensajes nuevos')} desde ${t}`,
    markAsRead: 'Marcar como leído',
    newBelow: (k) => `${k} ${plural(k, 'mensaje nuevo', 'mensajes nuevos')}`,
    markUnread: 'Marcar como no leído',
    join: 'Unirse',
    notJoined: 'No te has unido',
    online: 'En línea',
    away: 'Ausente',
    offline: 'Desconectado',
  },
  fr: {
    mention: (n, c) => `${n} vous a mentionné dans ${c}`,
    mentionDm: (n) => `${n} vous a mentionné`,
    mentionAnon: (c) => `Vous avez été mentionné dans ${c}`,
    dm: (n) => `${n} vous a envoyé un message`,
    message: (n, c) => `${n} a publié dans ${c}`,
    open: 'Ouvrir',
    unreadSince: (k, t) =>
      `${k} ${plural(k, 'nouveau message', 'nouveaux messages')} depuis ${t}`,
    markAsRead: 'Marquer comme lu',
    newBelow: (k) =>
      `${k} ${plural(k, 'nouveau message', 'nouveaux messages')}`,
    markUnread: 'Marquer comme non lu',
    join: 'Rejoindre',
    notJoined: 'Non rejoint',
    online: 'En ligne',
    away: 'Absent',
    offline: 'Hors ligne',
  },
  it: {
    mention: (n, c) => `${n} ti ha menzionato in ${c}`,
    mentionDm: (n) => `${n} ti ha menzionato`,
    mentionAnon: (c) => `Sei stato menzionato in ${c}`,
    dm: (n) => `${n} ti ha inviato un messaggio`,
    message: (n, c) => `${n} ha scritto in ${c}`,
    open: 'Apri',
    unreadSince: (k, t) =>
      `${k} ${plural(k, 'nuovo messaggio', 'nuovi messaggi')} da ${t}`,
    markAsRead: 'Segna come letto',
    newBelow: (k) => `${k} ${plural(k, 'nuovo messaggio', 'nuovi messaggi')}`,
    markUnread: 'Segna come non letto',
    join: 'Unisciti',
    notJoined: 'Non iscritto',
    online: 'Online',
    away: 'Assente',
    offline: 'Offline',
  },
  ko: {
    mention: (n, c) => `${n}님이 ${c}에서 회원님을 멘션했습니다`,
    mentionDm: (n) => `${n}님이 회원님을 멘션했습니다`,
    mentionAnon: (c) => `${c}에서 멘션되었습니다`,
    dm: (n) => `${n}님이 메시지를 보냈습니다`,
    message: (n, c) => `${n}님이 ${c}에 글을 올렸습니다`,
    open: '열기',
    unreadSince: (k, t) => `${t} 이후 새 메시지 ${k}개`,
    markAsRead: '읽음으로 표시',
    newBelow: (k) => `새 메시지 ${k}개`,
    markUnread: '읽지 않음으로 표시',
    join: '참여',
    notJoined: '참여하지 않음',
    online: '온라인',
    away: '자리 비움',
    offline: '오프라인',
  },
  de: {
    mention: (n, c) => `${n} hat dich in ${c} erwähnt`,
    mentionDm: (n) => `${n} hat dich erwähnt`,
    mentionAnon: (c) => `Du wurdest in ${c} erwähnt`,
    dm: (n) => `${n} hat dir eine Nachricht gesendet`,
    message: (n, c) => `${n} hat in ${c} geschrieben`,
    open: 'Öffnen',
    unreadSince: (k, t) =>
      `${k} ${plural(k, 'neue Nachricht', 'neue Nachrichten')} seit ${t}`,
    markAsRead: 'Als gelesen markieren',
    newBelow: (k) => `${k} ${plural(k, 'neue Nachricht', 'neue Nachrichten')}`,
    markUnread: 'Als ungelesen markieren',
    join: 'Beitreten',
    notJoined: 'Nicht beigetreten',
    online: 'Online',
    away: 'Abwesend',
    offline: 'Offline',
  },
  ja: {
    mention: (n, c) => `${n} さんが ${c} であなたをメンションしました`,
    mentionDm: (n) => `${n} さんがあなたをメンションしました`,
    mentionAnon: (c) => `${c} でメンションされました`,
    dm: (n) => `${n} さんからメッセージが届きました`,
    message: (n, c) => `${n} さんが ${c} に投稿しました`,
    open: '開く',
    unreadSince: (k, t) => `${t} 以降の新着メッセージ ${k} 件`,
    markAsRead: '既読にする',
    newBelow: (k) => `新着メッセージ ${k} 件`,
    markUnread: '未読にする',
    join: '参加',
    notJoined: '未参加',
    online: 'オンライン',
    away: '離席中',
    offline: 'オフライン',
  },
  hi: {
    mention: (n, c) => `${n} ने ${c} में आपका उल्लेख किया`,
    mentionDm: (n) => `${n} ने आपका उल्लेख किया`,
    mentionAnon: (c) => `${c} में आपका उल्लेख किया गया`,
    dm: (n) => `${n} ने आपको एक संदेश भेजा`,
    message: (n, c) => `${n} ने ${c} में पोस्ट किया`,
    open: 'खोलें',
    unreadSince: (k, t) => `${t} से ${k} नए संदेश`,
    markAsRead: 'पढ़ा हुआ चिह्नित करें',
    newBelow: (k) => `${k} नए संदेश`,
    markUnread: 'अपठित चिह्नित करें',
    join: 'जुड़ें',
    notJoined: 'जुड़े नहीं',
    online: 'ऑनलाइन',
    away: 'दूर',
    offline: 'ऑफ़लाइन',
  },
  ru: {
    mention: (n, c) => `${n} упомянул вас в ${c}`,
    mentionDm: (n) => `${n} упомянул вас`,
    mentionAnon: (c) => `Вас упомянули в ${c}`,
    dm: (n) => `${n} отправил вам сообщение`,
    message: (n, c) => `${n} написал в ${c}`,
    open: 'Открыть',
    unreadSince: (k, t) => `Новых сообщений: ${k} с ${t}`,
    markAsRead: 'Отметить как прочитанное',
    newBelow: (k) => `Новых сообщений: ${k}`,
    markUnread: 'Отметить как непрочитанное',
    join: 'Присоединиться',
    notJoined: 'Вы не участник',
    online: 'В сети',
    away: 'Отошёл',
    offline: 'Не в сети',
  },
  zh: {
    mention: (n, c) => `${n} 在 ${c} 中提到了你`,
    mentionDm: (n) => `${n} 提到了你`,
    mentionAnon: (c) => `有人在 ${c} 中提到了你`,
    dm: (n) => `${n} 给你发送了一条消息`,
    message: (n, c) => `${n} 在 ${c} 中发布了消息`,
    open: '打开',
    unreadSince: (k, t) => `自 ${t} 以来有 ${k} 条新消息`,
    markAsRead: '标记为已读',
    newBelow: (k) => `${k} 条新消息`,
    markUnread: '标记为未读',
    join: '加入',
    notJoined: '未加入',
    online: '在线',
    away: '离开',
    offline: '离线',
  },
  'zh-TW': {
    mention: (n, c) => `${n} 在 ${c} 中提到了你`,
    mentionDm: (n) => `${n} 提到了你`,
    mentionAnon: (c) => `有人在 ${c} 中提到了你`,
    dm: (n) => `${n} 傳送了一則訊息給你`,
    message: (n, c) => `${n} 在 ${c} 中發布了訊息`,
    open: '開啟',
    unreadSince: (k, t) => `自 ${t} 以來有 ${k} 則新訊息`,
    markAsRead: '標示為已讀',
    newBelow: (k) => `${k} 則新訊息`,
    markUnread: '標示為未讀',
    join: '加入',
    notJoined: '未加入',
    online: '線上',
    away: '離開',
    offline: '離線',
  },
  vi: {
    mention: (n, c) => `${n} đã nhắc đến bạn trong ${c}`,
    mentionDm: (n) => `${n} đã nhắc đến bạn`,
    mentionAnon: (c) => `Bạn được nhắc đến trong ${c}`,
    dm: (n) => `${n} đã gửi cho bạn một tin nhắn`,
    message: (n, c) => `${n} đã đăng trong ${c}`,
    open: 'Mở',
    unreadSince: (k, t) => `${k} tin nhắn mới kể từ ${t}`,
    markAsRead: 'Đánh dấu đã đọc',
    newBelow: (k) => `${k} tin nhắn mới`,
    markUnread: 'Đánh dấu chưa đọc',
    join: 'Tham gia',
    notJoined: 'Chưa tham gia',
    online: 'Trực tuyến',
    away: 'Vắng mặt',
    offline: 'Ngoại tuyến',
  },
  id: {
    mention: (n, c) => `${n} menyebut Anda di ${c}`,
    mentionDm: (n) => `${n} menyebut Anda`,
    mentionAnon: (c) => `Anda disebut di ${c}`,
    dm: (n) => `${n} mengirimi Anda pesan`,
    message: (n, c) => `${n} memposting di ${c}`,
    open: 'Buka',
    unreadSince: (k, t) => `${k} pesan baru sejak ${t}`,
    markAsRead: 'Tandai sudah dibaca',
    newBelow: (k) => `${k} pesan baru`,
    markUnread: 'Tandai belum dibaca',
    join: 'Gabung',
    notJoined: 'Belum bergabung',
    online: 'Online',
    away: 'Tidak di tempat',
    offline: 'Offline',
  },
};

export function getConnectNotifyCopy(locale: string): ConnectNotifyCopy {
  return COPY[normalizeLocale(locale)];
}
